// Does this garment belong on this body? — the pure half.
//
// A Marvelous garment is draped on whatever avatar the designer had open, and
// nothing in the export records which one. Put it on a different figure and it
// is simply the wrong size — but it still LOADS, still skins, and still
// renders, so the failure arrives as "the drape looks bad" rather than "this
// garment is not for this character". That is the confusion this module
// exists to prevent: it measures the garment against the target's own
// skeleton and says, in numbers, whether they agree.
//
// It REPORTS and does not act. A silent auto-rescale would make every garment
// "fit" and would hide exactly the fact the user needs — that the asset was
// authored for someone else. The suggested transform is advisory; the caller
// applies it only when explicitly asked to.
//
// Measured on Sara's isaac_tshirtJoggers export (ENG-1108): against Aoi (male
// MetaHuman, the only male body in the app with a real skinned rig) the span
// ratio is 1.06 and the joggers land on his own jeans to within a millimetre;
// against Myra it is 1.21, and that 21% is exactly what a reader needs to see
// rather than discover by looking at a stretched t-shirt.
//
// No three.js import here, so node:test drives the maths.

/**
 * The joints the body span is measured between, most-preferred first. Names
 * follow the Unreal/MetaHuman skeleton every rig in this app uses (verified on
 * myra: root, pelvis, spine_01..05, neck_01, head, clavicle_l, …).
 */
export const BODY_LANDMARK_JOINTS = Object.freeze({
  neck: Object.freeze(['neck_01', 'neck_1', 'spine_05']),
  ankle: Object.freeze(['foot_l', 'foot_L', 'ankle_l', 'calf_l']),
  shoulder: Object.freeze(['upperarm_l', 'clavicle_l']),
})

/**
 * How far from a perfect match still counts as "fits".
 *
 * `spanMax` is generous upward because a shirt's collar genuinely stands ABOVE
 * the neck joint it is measured to — Sara's export clears Aoi's neck by 13 cm
 * of neckband and still fits him. `offsetMax` is the hem-to-ankle slack: a
 * jogger cuff sits a few centimetres up the leg.
 */
export const FIT_TOLERANCE = Object.freeze({ spanMin: 0.9, spanMax: 1.15, offsetMax: 0.12 })

/**
 * Does `boneName` name one of `candidates`, allowing for three's uniquifying
 * suffix?
 *
 * GLTFLoader renames a node whose name is already taken, so a GLB carrying
 * several copies of one skeleton gets `root`, `root_1`, `root_2`… — measured on
 * aoi-fridaytest, which ships FIVE 342-bone copies (body, tee, jeans, loafers,
 * and the face/hair rig) and whose body skin is the only one still called
 * `neck_01`. A landmark lookup that matched exactly found nothing on four of
 * the five and reported "this skeleton has no neck", which is a confusing way
 * to say "you are looking at the wrong skeleton".
 *
 * @param {string} boneName
 * @param {readonly string[]} candidates
 */
/**
 * How well `boneName` answers ONE candidate: 0 exact, 1 through GLTFLoader's
 * uniquifying suffix, 2 through a rig-wide name prefix, -1 not at all. Lower
 * wins, so a clean rig is never decided by the loosest rule.
 */
function landmarkRank(boneName, candidate) {
  if (!boneName) return -1
  if (boneName === candidate) return 0
  // GLTFLoader uniquifies duplicate node names: `neck_01_2`. Note MetaHuman
  // bones genuinely END in digits (`spine_01`), so this strips at most one
  // group and is only ever consulted as a fallback.
  if (boneName.replace(/_\d+$/, '') === candidate) return 1
  // A Maya-sourced rig prefixes every bone with its namespace, and the
  // separator does NOT survive: Isaac's `DHIbody:neck_01` reaches three as
  // `DHIbodyneck_01`, because the loader strips the colon. With no separator
  // left there is nothing to split on, so the candidate is matched as a
  // SUFFIX. Safe for this candidate set — no MetaHuman bone ends in another's
  // name (`lowerarm_l` does not end in `upperarm_l`, `calf_twist_01_l` does not
  // end in `calf_l`) — and anything it does catch, such as an IK rig's
  // `ik_foot_l`, is the same joint in the same place.
  if (boneName.endsWith(candidate)) return 2
  if (boneName.replace(/_\d+$/, '').endsWith(candidate)) return 2
  return -1
}

export function matchesLandmark(boneName, candidates) {
  return candidates.some((c) => landmarkRank(boneName, c) >= 0)
}

/**
 * Index of the bone that best answers `candidates`, or -1.
 *
 * `candidates` is ordered MOST-PREFERRED FIRST and that order is the contract:
 * the ankle list ends in `calf_l` as a fallback for a skeleton with no foot, so
 * scanning the BONES and taking the first that matches anything returns the
 * knee on every skeleton that has both — `calf_l` sits at bone 277 and `foot_l`
 * at 278. Measured on aoi-fridaytest, that one inversion (plus the same on the
 * neck, where `spine_05` precedes `neck_01`) reported his neck-to-ankle span as
 * 0.863 m instead of 1.379 m, and turned a garment that fits into a "70%
 * oversize" mismatch. So the candidates are the outer loop, always.
 *
 * @param {ArrayLike<{name: string}>} bones
 * @param {readonly string[]} candidates
 */
export function pickLandmarkBone(bones, candidates) {
  for (const c of candidates) {
    // Within one candidate, the tightest match wins — an exact `foot_l` beats a
    // prefixed `DHIbodyfoot_l` on a rig that somehow has both.
    let best = -1, bestRank = Infinity
    for (let i = 0; i < bones.length; i++) {
      const r = landmarkRank(bones[i]?.name, c)
      if (r >= 0 && r < bestRank) { bestRank = r; best = i }
      if (bestRank === 0) break
    }
    if (best >= 0) return best
  }
  return -1
}

/**
 * Compare a garment's bounding box against a body's landmark joints.
 *
 * @param {object} args
 * @param {{ minY:number, maxY:number, halfX:number }} args.garment  bbox in METRES (post garmentUnits)
 * @param {{ ankleY:number, neckY:number, shoulderHalfX:number }} args.body  joint world positions
 * @returns {{ verdict: 'fits'|'mismatch'|'misplaced'|'unknown', spanRatio: number|null,
 *             hemOffset: number, widthRatio: number|null, summary: string,
 *             suggested: { scale: number, offsetY: number } }}
 */
export function fitReport({ garment, body }) {
  const garmentSpan = garment.maxY - garment.minY
  const bodySpan = body.neckY - body.ankleY
  const suggestedIdentity = { scale: 1, offsetY: 0 }

  if (!(garmentSpan > 0) || !(bodySpan > 0)) {
    return {
      verdict: 'unknown',
      spanRatio: null,
      hemOffset: 0,
      widthRatio: null,
      summary: `cannot judge fit: garment span ${garmentSpan.toFixed(3)} m, body span ${bodySpan.toFixed(3)} m `
        + '(one of them is degenerate — is the rig posed, or the garment empty?)',
      suggested: suggestedIdentity,
    }
  }

  const spanRatio = garmentSpan / bodySpan
  const scale = 1 / spanRatio
  // The transform that would put the garment's hem exactly on the ankle, applied
  // about the world origin: p -> p * scale + offsetY.
  const suggested = { scale, offsetY: body.ankleY - garment.minY * scale }
  // Measure placement independently of size, so "too big" and "in the wrong
  // place" are not reported as the same problem. Scale the garment first, then
  // ask where its hem landed.
  const hemOffset = garment.minY - body.ankleY
  const hemOffsetAtSize = garment.minY * scale - body.ankleY
  const widthRatio = body.shoulderHalfX > 0 ? garment.halfX / body.shoulderHalfX : null

  const sized = spanRatio >= FIT_TOLERANCE.spanMin && spanRatio <= FIT_TOLERANCE.spanMax
  const placed = Math.abs(hemOffset) <= FIT_TOLERANCE.offsetMax
  const placedAtSize = Math.abs(hemOffsetAtSize) <= FIT_TOLERANCE.offsetMax

  const pct = Math.round(Math.abs(spanRatio - 1) * 100)
  const sizeWord = spanRatio > 1 ? 'oversize' : 'undersize'
  const nums = `garment ${garmentSpan.toFixed(3)} m tall vs body neck-to-ankle ${bodySpan.toFixed(3)} m `
    + `(ratio ${spanRatio.toFixed(3)}); hem sits ${(hemOffset * 100).toFixed(1)} cm above the ankle`

  if (sized && placed) {
    return { verdict: 'fits', spanRatio, hemOffset, widthRatio, suggested, summary: `fits — ${nums}` }
  }
  if (sized && !placed) {
    // Right size, wrong origin: the export is written about its own origin
    // rather than the avatar's. Rescaling would not help.
    return {
      verdict: 'misplaced',
      spanRatio, hemOffset, widthRatio,
      suggested: { scale: 1, offsetY: -hemOffset },
      summary: `misplaced — the size is right but the origin is not: ${nums}`,
    }
  }
  if (!sized && placedAtSize) {
    return {
      verdict: 'mismatch', spanRatio, hemOffset, widthRatio, suggested,
      summary: `mismatch — ${pct}% ${sizeWord} for this body: ${nums}. `
        + 'This garment was draped on a different figure.',
    }
  }
  return {
    verdict: 'mismatch', spanRatio, hemOffset, widthRatio, suggested,
    summary: `mismatch — ${pct}% ${sizeWord} and misplaced: ${nums}. `
      + 'This garment was draped on a different figure.',
  }
}
